// AuthContext.js
import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance, { authLoginRequest } from './axiosConfig';
import { clearLocalSession, consumeSsoTokensFromUrl, redirectToUnifiedAuth } from './utils/unifiedAuth';

export const AuthContext = createContext();

function AuthProvider({ children }) {
    const [authTokens, setAuthTokens] = useState(() => {
        const ssoTokens = consumeSsoTokensFromUrl();
        if (ssoTokens?.access) {
            return ssoTokens;
        }

        try {
            const tokens = localStorage.getItem('authTokens');
            return tokens ? JSON.parse(tokens) : null;
        } catch (error) {
            console.error('Error parsing auth tokens:', error);
            localStorage.removeItem('authTokens');
            return null;
        }
    });

    const [user, setUser] = useState(null);
    const [selectedServiceCenter, setSelectedServiceCenter] = useState(() => {
        try {
            const center = localStorage.getItem('selectedServiceCenter');
            return center ? JSON.parse(center) : null;
        } catch (error) {
            console.error('Error parsing service center:', error);
            localStorage.removeItem('selectedServiceCenter');
            return null;
        }
    });
    const [serviceCenters, setServiceCenters] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [projects, setProjects] = useState([]);
    const [isSelectingServiceCenter, setIsSelectingServiceCenter] = useState(false);
    const [otpRequired, setOtpRequired] = useState(false);
    const [telegramOtpEnabled, setTelegramOtpEnabled] = useState(true);
    const [isFetchingUserInfo, setIsFetchingUserInfo] = useState(false);
    const [isFetchingProjects, setIsFetchingProjects] = useState(false);
    const [hasFetchedUser, setHasFetchedUser] = useState(false);
    const [hasFetchedProjects, setHasFetchedProjects] = useState(false);
    const [componentsUpdated, setComponentsUpdated] = useState(false);
    const [error, setError] = useState('');
    const [otpStatusChecked, setOtpStatusChecked] = useState(false);
    const [hasAttemptedUserInfo, setHasAttemptedUserInfo] = useState(false);
    const [hasAttemptedProjects, setHasAttemptedProjects] = useState(false);
    const [authBootstrapComplete, setAuthBootstrapComplete] = useState(() => !Boolean(authTokens?.access));

    const otpCheckInFlightRef = useRef(false);
    const userInfoInFlightRef = useRef(false);
    const projectsInFlightRef = useRef(false);

    const navigate = useNavigate();

    const getCurrentDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const clearAllData = useCallback(() => {
        otpCheckInFlightRef.current = false;
        userInfoInFlightRef.current = false;
        projectsInFlightRef.current = false;

        setAuthTokens(null);
        setUser(null);
        setSelectedServiceCenter(null);
        setServiceCenters([]);
        setProjects([]);
        setOtpRequired(false);
        setHasFetchedUser(false);
        setHasFetchedProjects(false);
        setOtpStatusChecked(false);
        setHasAttemptedUserInfo(false);
        setHasAttemptedProjects(false);
        setIsFetchingUserInfo(false);
        setIsFetchingProjects(false);
        setLoading(false);
        setLoadingProjects(false);
        setError('');
        setAuthBootstrapComplete(true);
        clearLocalSession();
    }, []);

    const logoutUser = useCallback(() => {
        setLoading(true);
        clearAllData();
        setTimeout(() => {
            setLoading(false);
            redirectToUnifiedAuth('/');
        }, 100);
    }, [clearAllData]);

    const fetchUserInfo = useCallback(async (tokens) => {
        if (!tokens?.access || userInfoInFlightRef.current) return;

        userInfoInFlightRef.current = true;
        setIsFetchingUserInfo(true);
        setLoading(true);

        try {
            const response = await axiosInstance.get('/api/user-info/', {
                headers: { 'Authorization': `Bearer ${tokens.access}` }
            });

            if (response.status === 200) {
                const { username, email, groups, selected_service_center, service_centers } = response.data;
                setUser({ username, email, groups });
                setServiceCenters(service_centers || []);
                localStorage.setItem('user', JSON.stringify({ username, email, groups, service_centers }));
                setHasFetchedUser(true);

                if (selected_service_center && selected_service_center.id) {
                    setSelectedServiceCenter(selected_service_center);
                    localStorage.setItem('selectedServiceCenter', JSON.stringify(selected_service_center));
                    setHasFetchedProjects(false);
                    setHasAttemptedProjects(false);
                } else {
                    setSelectedServiceCenter(null);
                    localStorage.removeItem('selectedServiceCenter');
                    setHasFetchedProjects(false);
                    setHasAttemptedProjects(false);
                    navigate('/select-service-center');
                }
            } else {
                throw new Error('Invalid response status');
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error);
            if (error.response?.status === 401) {
                clearAllData();
                redirectToUnifiedAuth('/');
            }
        } finally {
            userInfoInFlightRef.current = false;
            setIsFetchingUserInfo(false);
            setLoading(false);
            setHasAttemptedUserInfo(true);
        }
    }, [navigate, clearAllData]);

    const fetchProjects = useCallback(async (tokens, serviceCenterId) => {
        if (!tokens?.access || !serviceCenterId || projectsInFlightRef.current) return;

        projectsInFlightRef.current = true;
        setIsFetchingProjects(true);
        setLoading(true);
        setLoadingProjects(true);

        try {
            const response = await axiosInstance.get(`/api/projects/?service_center=${serviceCenterId}`, {
                headers: { 'Authorization': `Bearer ${tokens.access}` }
            });
            if (response.status === 200) {
                setProjects(response.data.projects || []);
                setHasFetchedProjects(true);
            } else {
                console.error('Failed to fetch projects, status:', response.status);
            }
        } catch (error) {
            console.error('Failed to fetch projects:', error);
            if (error.response?.status === 401) {
                clearAllData();
                redirectToUnifiedAuth('/');
            }
        } finally {
            projectsInFlightRef.current = false;
            setIsFetchingProjects(false);
            setLoading(false);
            setLoadingProjects(false);
            setHasAttemptedProjects(true);
        }
    }, [clearAllData]);

    /** GET /api/check-otp-status/ */
    const checkOtpStatus = useCallback(async (tokens) => {
        if (!tokens?.access || otpCheckInFlightRef.current || otpStatusChecked) {
            return;
        }

        otpCheckInFlightRef.current = true;

        try {
            const response = await axiosInstance.get('/api/check-otp-status/', {
                headers: { 'Authorization': `Bearer ${tokens.access}` }
            });

            const otpEnabled = response.data.telegram_otp_enabled !== false;
            const requiresOtp = response.data.otp_required ?? response.data.otp_verified === false;
            setTelegramOtpEnabled(otpEnabled);

            if (requiresOtp) {
                setOtpRequired(true);
                navigate('/otp-verification');
            } else {
                setOtpRequired(false);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                try {
                    await axiosInstance.post('/api/session/flush/', {}, { withCredentials: true });
                    const otpResponse = await axiosInstance.post('/api/send-otp/', {}, {
                        headers: { 'Authorization': `Bearer ${tokens.access}` }
                    });
                    const requiresOtp = otpResponse.data?.otp_required !== false;
                    setTelegramOtpEnabled(otpResponse.data?.telegram_otp_enabled !== false);
                    setOtpRequired(requiresOtp);
                    if (requiresOtp) {
                        navigate('/otp-verification');
                    }
                } catch {
                    clearAllData();
                    redirectToUnifiedAuth('/');
                }
            } else {
                console.error('Ошибка при проверке статуса OTP:', error);
            }
        } finally {
            otpCheckInFlightRef.current = false;
            setOtpStatusChecked(true);
        }
    }, [navigate, clearAllData, otpStatusChecked]);

    useEffect(() => {
        const lastLoginDate = localStorage.getItem('lastLoginDate');
        const currentDate = getCurrentDate();

        if (lastLoginDate && lastLoginDate !== currentDate) {
            clearAllData();
        } else if (authTokens) {
            localStorage.setItem('lastLoginDate', currentDate);
        }
    }, [clearAllData, authTokens]);

    useEffect(() => {
        if (!authTokens?.access) {
            setOtpStatusChecked(false);
            setHasAttemptedUserInfo(false);
            setHasAttemptedProjects(false);
            setAuthBootstrapComplete(true);
            return;
        }

        setAuthBootstrapComplete(false);
        setOtpStatusChecked(false);
        setHasFetchedUser(false);
        setHasFetchedProjects(false);
        setHasAttemptedUserInfo(false);
        setHasAttemptedProjects(false);
    }, [authTokens?.access]);

    useEffect(() => {
        if (!authTokens?.access || otpStatusChecked) {
            return;
        }
        checkOtpStatus(authTokens);
    }, [authTokens, otpStatusChecked, checkOtpStatus]);

    useEffect(() => {
        if (!authTokens?.access || !otpStatusChecked || otpRequired || hasFetchedUser || isFetchingUserInfo || hasAttemptedUserInfo) {
            return;
        }
        fetchUserInfo(authTokens);
    }, [authTokens, otpStatusChecked, otpRequired, hasFetchedUser, isFetchingUserInfo, hasAttemptedUserInfo, fetchUserInfo]);

    useEffect(() => {
        if (!selectedServiceCenter?.id || !authTokens?.access || !otpStatusChecked || otpRequired || !hasFetchedUser || hasFetchedProjects || isFetchingProjects || hasAttemptedProjects) {
            return;
        }
        fetchProjects(authTokens, selectedServiceCenter.id);
    }, [selectedServiceCenter, authTokens, otpStatusChecked, otpRequired, hasFetchedUser, hasFetchedProjects, isFetchingProjects, hasAttemptedProjects, fetchProjects]);

    useEffect(() => {
        if (componentsUpdated && selectedServiceCenter?.id && authTokens?.access && !isFetchingProjects) {
            setHasFetchedProjects(false);
            setHasAttemptedProjects(false);
            fetchProjects(authTokens, selectedServiceCenter.id);
            setComponentsUpdated(false);
        }
    }, [componentsUpdated, authTokens, selectedServiceCenter, fetchProjects, isFetchingProjects]);

    useEffect(() => {
        if (!authTokens?.access) {
            setAuthBootstrapComplete(true);
            return;
        }

        if (!otpStatusChecked) {
            return;
        }

        if (otpRequired) {
            setAuthBootstrapComplete(true);
            return;
        }

        if (isFetchingUserInfo) {
            return;
        }

        if (!hasFetchedUser && !hasAttemptedUserInfo) {
            return;
        }

        const requiresProjects = Boolean(selectedServiceCenter?.id && hasFetchedUser);
        if (requiresProjects) {
            if (isFetchingProjects) {
                return;
            }
            if (!hasFetchedProjects && !hasAttemptedProjects) {
                return;
            }
        }

        setAuthBootstrapComplete(true);
    }, [
        authTokens?.access,
        otpStatusChecked,
        otpRequired,
        isFetchingUserInfo,
        hasFetchedUser,
        hasAttemptedUserInfo,
        selectedServiceCenter?.id,
        isFetchingProjects,
        hasFetchedProjects,
        hasAttemptedProjects,
    ]);

    /** Логин + отправка OTP */
    const loginUser = async (username, password) => {
        setLoading(true);
        setError('');
        clearAllData();

        try {
            // На всякий случай — сброс серверной сессии
            try {
                await axiosInstance.post('/api/session/flush/', {}, { withCredentials: true });
            } catch {}

            // JWT логин (cookies не нужны)
            const response = await authLoginRequest(username, password);

            if (response.data?.access) {
                const tokens = response.data;
                // если refresh не пришёл (некоторые конфиги отдают только access) — не падаем
                if (!tokens.refresh) {
                    const prev = JSON.parse(localStorage.getItem('authTokens') || 'null');
                    if (prev?.refresh) tokens.refresh = prev.refresh;
                }

                setAuthTokens(tokens);
                localStorage.setItem('authTokens', JSON.stringify(tokens));
                localStorage.setItem('lastLoginDate', getCurrentDate());

                // Отправка OTP — авторизация Bearer + нужны cookies (withCredentials: true)
                try {
                    const otpResponse = await axiosInstance.post('/api/send-otp/', {}, {
                        headers: { 'Authorization': `Bearer ${tokens.access}` },
                        withCredentials: true,
                        timeout: 10000
                    });

                    if (otpResponse.data?.success) {
                        const requiresOtp = otpResponse.data?.otp_required !== false;
                        setTelegramOtpEnabled(otpResponse.data?.telegram_otp_enabled !== false);
                        setOtpRequired(requiresOtp);
                        setOtpStatusChecked(true);
                        if (requiresOtp) {
                            localStorage.setItem('otpLastSentTime', Date.now().toString());
                        }
                        return true;
                    } else {
                        setError('Не удалось отправить OTP код. Пожалуйста, попробуйте еще раз.');
                        return false;
                    }
                } catch (otpError) {
                    console.error('OTP error:', otpError);
                    if (otpError.response?.status === 401) {
                        try {
                            await axiosInstance.post('/api/session/flush/', {}, { withCredentials: true });
                            const retry = await axiosInstance.post('/api/send-otp/', {}, {
                                headers: { 'Authorization': `Bearer ${tokens.access}` },
                                withCredentials: true
                            });
                            if (retry.data?.success) {
                                const requiresOtp = retry.data?.otp_required !== false;
                                setTelegramOtpEnabled(retry.data?.telegram_otp_enabled !== false);
                                setOtpRequired(requiresOtp);
                                setOtpStatusChecked(true);
                                if (requiresOtp) {
                                    localStorage.setItem('otpLastSentTime', Date.now().toString());
                                }
                                return true;
                            }
                        } catch {}
                    }
                    setError('Ошибка при отправке OTP. Попробуйте еще раз.');
                    return false;
                }
            } else {
                setError('Неверное имя пользователя или пароль.');
                return false;
            }
        } catch (error) {
            console.error('Ошибка при входе в систему:', error);
            if (error.code === 'ECONNABORTED') {
                setError('Превышено время ожидания. Проверьте подключение к интернету.');
            } else if (error.response?.status === 401) {
                setError('Неверное имя пользователя или пароль.');
            } else if (error.response?.status >= 500) {
                setError('Ошибка сервера. Попробуйте позже.');
            } else {
                setError('Ошибка при входе. Попробуйте еще раз.');
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    /** POST /api/verify-otp/ */
    const verifyOtp = async (otpCode) => {
        if (!authTokens?.access) {
            setError('Сессия истекла. Пожалуйста, войдите снова.');
            clearAllData();
            redirectToUnifiedAuth('/');
            return false;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.post('/api/verify-otp/', { otp: otpCode }, {
                headers: { 'Authorization': `Bearer ${authTokens.access}` },
                withCredentials: true,
                timeout: 10000
            });

            if (response.data.success) {
                setAuthBootstrapComplete(false);
                setOtpRequired(false);
                setOtpStatusChecked(true);
                setHasFetchedUser(false);
                setHasAttemptedUserInfo(false);
                setHasFetchedProjects(false);
                setHasAttemptedProjects(false);

                if (selectedServiceCenter) {
                    navigate('/');
                } else {
                    navigate('/select-service-center');
                }
                return true;
            } else {
                throw new Error('Неверный код OTP.');
            }
        } catch (error) {
            console.error('Ошибка при проверке OTP:', error);
            if (error.response?.status === 401) {
                try {
                    await axiosInstance.post('/api/session/flush/', {}, { withCredentials: true });
                } catch {}
                setError('Сессия подтверждения истекла. Отправьте код заново.');
                setOtpRequired(true);
            } else {
                setError('Неверный код OTP или он устарел.');
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const selectServiceCenter = async (center) => {
        if (!center?.id) {
            console.error('Invalid service center selected:', center);
            return;
        }
        if (isSelectingServiceCenter) return;

        setIsSelectingServiceCenter(true);
        setLoading(true);

        try {
            await axiosInstance.post('/api/set-service-center/', { service_center_id: center.id }, {
                headers: { 'Authorization': `Bearer ${authTokens.access}` },
                withCredentials: true,
                timeout: 10000
            });

            setSelectedServiceCenter(center);
            localStorage.setItem('selectedServiceCenter', JSON.stringify(center));
            setHasFetchedProjects(false);
            setHasAttemptedProjects(false);
            await fetchProjects(authTokens, center.id);
            navigate('/');
        } catch (error) {
            console.error('Failed to set service center:', error);
            if (error.response?.status === 401) {
                clearAllData();
                redirectToUnifiedAuth('/');
            }
        } finally {
            setIsSelectingServiceCenter(false);
            setLoading(false);
        }
    };

    const authContextData = {
        user,
        authTokens,
        loginUser,
        logoutUser,
        selectedServiceCenter,
        selectServiceCenter,
        serviceCenters,
        projects,
        setProjects,
        loadingProjects,
        loading,
        authBootstrapComplete,
        otpRequired,
        setOtpRequired,
        telegramOtpEnabled,
        verifyOtp,
        setComponentsUpdated,
        error,
    };

    return (
        <AuthContext.Provider value={authContextData}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthProvider;
